import map from 'lodash/map'
import size from 'lodash/size'
import Script from 'next/script'
import React, { useContext, useMemo } from 'react'

import { generateProductLinkingDataImage } from '@Components/Image'
import { ProductSearchContext } from '@Globals/Algolia/Context'
import { BlockConfigContext } from '@Globals/CMS/Context'
import { MetadataContext } from '@Globals/Metadata'
import { usePageCanonical } from '@Globals/Page/Canonical'

export const LinkedData = ({
  description,
  title,
}: {
  description: string
  title: string
}) => {
  const { hitsArray } = useContext(ProductSearchContext)

  const { page, store: { config: { currencyCode = '' } = {} } = {}, storeURL } = useContext(MetadataContext)
  const blockConfig = useContext(BlockConfigContext)
  // The same URL as <link rel="canonical">, so the structured data never points at a filtered or tracked variant.
  const { url: canonicalURL } = usePageCanonical(page, blockConfig)

  const json = useMemo((): string | null => {
    if (!hitsArray || !size(hitsArray)) {
      return null
    }

    const dataObject = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      url: canonicalURL,
      name: title,
      description,
      itemListElement: map(
        hitsArray,
        (product, productIndex) => ({
          '@type': 'ListItem',
          position: productIndex + 1,
          item: {
            '@type': 'Product',
            name: product.title,
            brand: {
              '@type': 'Brand',
              name: product.brandTitle,
            },
            category: product.category || '',
            url: storeURL(product.url),
            sku: product.id,
            image: generateProductLinkingDataImage(product.primaryImage ?? ''),
            offers: {
              '@type': 'Offer',
              availability: `https://schema.org/${product.isOutOfStock ? 'OutOfStock' : 'InStock'}`,
              price: product.priceValue,
              priceCurrency: currencyCode,
            },
          },
        }),
      ),
    }

    return JSON.stringify(dataObject)
  }, [hitsArray, storeURL, canonicalURL, title, description, currencyCode])

  if (!json) { return null }

  return (
    <Script id={`category-json-ld-${title}`} type="application/ld+json">
      {json}
    </Script>
  )
}
